import { useCallback, useRef } from "react";
import {
  MessageBannerVariant,
  useToastContext,
} from "@pufferfinance/puffer-ui-components";
import {
  parseWalletError,
  ParsedWalletError,
  ToastSeverity,
} from "../utils/parseWalletError";

const TIMEOUT_BY_SEVERITY: Record<ToastSeverity, number> = {
  success: 5000,
  info: 5000,
  warning: 7000,
  error: 12000,
};

const VARIANT_BY_SEVERITY: Record<ToastSeverity, MessageBannerVariant> = {
  success: MessageBannerVariant.Info,
  info: MessageBannerVariant.Info,
  warning: MessageBannerVariant.Warning,
  error: MessageBannerVariant.Error,
};

// Suppress an identical toast if it was shown within this window. Stops
// repeated failures (e.g. RPC rate-limit retries) from stacking the same
// banner over and over.
const DEDUPE_WINDOW_MS = 8000;

export const useAppToast = () => {
  const toastState = useToastContext();
  const lastShownRef = useRef<Map<string, number>>(new Map());

  const show = useCallback(
    (message: string, severity: ToastSeverity) => {
      const key = `${severity}:${message}`;
      const now = Date.now();
      const lastShown = lastShownRef.current.get(key);
      if (lastShown !== undefined && now - lastShown < DEDUPE_WINDOW_MS) {
        return;
      }
      lastShownRef.current.set(key, now);

      toastState.add(
        { label: message, variant: VARIANT_BY_SEVERITY[severity] },
        { timeout: TIMEOUT_BY_SEVERITY[severity] },
      );
    },
    [toastState],
  );

  const showError = useCallback((m: string) => show(m, "error"), [show]);
  const showSuccess = useCallback((m: string) => show(m, "success"), [show]);
  const showInfo = useCallback((m: string) => show(m, "info"), [show]);
  const showWarning = useCallback((m: string) => show(m, "warning"), [show]);

  const showWalletError = useCallback(
    (error: unknown, context = "Transaction"): ParsedWalletError => {
      const parsed = parseWalletError(error, context);
      show(parsed.message, parsed.severity);
      return parsed;
    },
    [show],
  );

  return {
    showError,
    showSuccess,
    showInfo,
    showWarning,
    showWalletError,
  };
};
